import type { Language } from './types';

export type TranslationKey =
  | 'app.title'
  | 'app.subtitle'
  | 'app.disclaimer'
  | 'lang.label'
  | 'lang.changed'
  | 'common.error'
  | 'common.close'
  | 'search.title'
  | 'search.pair'
  | 'search.pairPlaceholder'
  | 'search.timeframe'
  | 'search.limit'
  | 'search.search'
  | 'search.loading'
  | 'search.noMatch'
  | 'search.noDataTitle'
  | 'search.noDataDesc'
  | 'search.loadDoneTitle'
  | 'search.loadDoneDesc'
  | 'chart.title'
  | 'chart.mode.bottom'
  | 'chart.mode.top'
  | 'chart.autoDetect'
  | 'chart.reset'
  | 'chart.instructionsTitle'
  | 'chart.instructions.1'
  | 'chart.instructions.2'
  | 'chart.instructions.3'
  | 'chart.instructions.4'
  | 'chart.loadingTitle'
  | 'chart.loadingDesc'
  | 'chart.selectedPoint'
  | 'chart.selectedMessage'
  | 'chart.autoDetect.noClearBottom'
  | 'chart.autoDetect.noClearTop'
  | 'chart.autoDetect.noSuitableLow'
  | 'chart.autoDetect.noSuitableHigh'
  | 'chart.autoDetect.failed'
  | 'chart.selectedTime'
  | 'chart.entryRange'
  | 'tooltip.time'
  | 'tooltip.open'
  | 'tooltip.high'
  | 'tooltip.low'
  | 'tooltip.close'
  | 'table.title'
  | 'table.line'
  | 'table.price'
  | 'table.type'
  | 'table.status'
  | 'table.type.target'
  | 'table.type.pivot'
  | 'table.status.reached'
  | 'table.status.notReached'
  | 'tip.title'
  | 'tip.desc.top'
  | 'tip.desc.bottom'
  | 'summary.title'
  | 'summary.section.analysis'
  | 'summary.section.action'
  | 'summary.progress.targets'
  | 'summary.progress.pivots'
  | 'summary.reached.targets'
  | 'summary.reached.pivots'
  | 'summary.disclaimer';

type Dict = Record<TranslationKey, string>;

export const DEFAULT_LANGUAGE: Language = 'zh-TW';
export const STORAGE_KEY = 'crypto_prediction_lang';

export const TRANSLATIONS: Record<Language, Dict> = {
  'zh-TW': {
    'app.title': '交易趨勢預測系統',
    'app.subtitle': '基於 K 線結構與預測線的趨勢研判工具',
    'app.disclaimer': '風險提示：加密貨幣交易具有高風險，本工具僅供參考，不構成投資建議。',
    'lang.label': '語言',
    'lang.changed': '語言已切換',
    'common.error': '錯誤',
    'common.close': '關閉',
    'search.title': '趨勢預測',
    'search.pair': '交易對',
    'search.pairPlaceholder': '選擇或輸入交易對',
    'search.timeframe': '時間週期',
    'search.limit': 'K線數量',
    'search.search': '搜尋',
    'search.loading': '載入資料中…',
    'search.noMatch': '找不到匹配交易對',
    'search.noDataTitle': '未找到資料',
    'search.noDataDesc': '未能取得 K 線資料。請更換交易對或時間週期後再試。',
    'search.loadDoneTitle': '資料載入完成',
    'search.loadDoneDesc': '已載入 {count} 筆 K 線資料，現在可以在圖表上手動選擇分型點。',
    'chart.title': 'K線圖 - 手動選擇{mode}預測',
    'chart.mode.bottom': '上漲',
    'chart.mode.top': '下跌',
    'chart.autoDetect': '自動識別',
    'chart.reset': '重置選擇',
    'chart.instructionsTitle': '使用說明',
    'chart.instructions.1': '請先選擇要標記的趨勢方向（上漲/下跌）',
    'chart.instructions.2': '點擊「自動識別」進行趨勢點識別',
    'chart.instructions.3': '亦可直接點擊圖表上的 K 線手動選擇',
    'chart.instructions.4': '選擇後系統會計算預測線並標示目標位/拐點',
    'chart.loadingTitle': '資料載入中',
    'chart.loadingDesc': '正在取得 K 線資料，請稍候…',
    'chart.selectedPoint': '{mode}預測點',
    'chart.selectedMessage': '已選擇第{index}根 K 線作為{mode}預測點',
    'chart.autoDetect.noClearBottom': '未找到明顯的上漲預測結構，將使用最低點',
    'chart.autoDetect.noClearTop': '未找到明顯的下跌預測結構，將使用最高點',
    'chart.autoDetect.noSuitableLow': '未找到合適的低點，請嘗試手動選擇',
    'chart.autoDetect.noSuitableHigh': '未找到合適的高點，請嘗試手動選擇',
    'chart.autoDetect.failed': '自動識別失敗，請嘗試手動選擇',
    'chart.selectedTime': '選擇時間',
    'chart.entryRange': '進場區間',
    'tooltip.time': '時間',
    'tooltip.open': '開盤價',
    'tooltip.high': '最高價',
    'tooltip.low': '最低價',
    'tooltip.close': '收盤價',
    'table.title': '交易趨勢預測系統 - {mode}預測線',
    'table.line': '線號',
    'table.price': '價格',
    'table.type': '類型',
    'table.status': '狀態',
    'table.type.target': '目標位',
    'table.type.pivot': '拐點',
    'table.status.reached': '已到達',
    'table.status.notReached': '未到達',
    'tip.title': '交易提示',
    'tip.desc.top': '拐點位置可能出現反彈；目標位可作為空頭減倉或止盈參考。',
    'tip.desc.bottom': '拐點位置可能出現回調；目標位可作為多頭減倉或止盈參考。',
    'summary.title': '趨勢分析總結',
    'summary.section.analysis': '{mode}趨勢分析',
    'summary.section.action': '操作建議',
    'summary.progress.targets': '目標位達成率',
    'summary.progress.pivots': '拐點達成率',
    'summary.reached.targets': '已達到 {reached}/{total} 個目標位',
    'summary.reached.pivots': '已達到 {reached}/{total} 個拐點',
    'summary.disclaimer': '免責聲明：以上分析僅供參考，不構成投資建議。投資有風險，入市需謹慎。',
  },
  en: {
    'app.title': 'Trading Trend Forecast',
    'app.subtitle': 'A K-line based trend analysis tool with projection levels',
    'app.disclaimer': 'Risk notice: Crypto trading is high-risk. This tool is for reference only and is not investment advice.',
    'lang.label': 'Language',
    'lang.changed': 'Language switched',
    'common.error': 'Error',
    'common.close': 'Close',
    'search.title': 'Trend Forecast',
    'search.pair': 'Pair',
    'search.pairPlaceholder': 'Select or type a trading pair',
    'search.timeframe': 'Timeframe',
    'search.limit': 'Candles',
    'search.search': 'Search',
    'search.loading': 'Loading…',
    'search.noMatch': 'No matching pairs',
    'search.noDataTitle': 'No data',
    'search.noDataDesc': 'No candles returned. Try a different pair or timeframe.',
    'search.loadDoneTitle': 'Loaded',
    'search.loadDoneDesc': 'Loaded {count} candles. You can now pick a pivot point on the chart.',
    'chart.title': 'Candlestick Chart - Manual {mode} Setup',
    'chart.mode.bottom': 'Bullish',
    'chart.mode.top': 'Bearish',
    'chart.autoDetect': 'Auto detect',
    'chart.reset': 'Reset',
    'chart.instructionsTitle': 'How to use',
    'chart.instructions.1': 'Choose direction (bullish/bearish)',
    'chart.instructions.2': 'Click “Auto detect” to find a candidate point',
    'chart.instructions.3': 'Or click a candle directly to select manually',
    'chart.instructions.4': 'Projection levels will be calculated as targets/pivots',
    'chart.loadingTitle': 'Loading',
    'chart.loadingDesc': 'Fetching candles, please wait…',
    'chart.selectedPoint': '{mode} point',
    'chart.selectedMessage': 'Selected candle #{index} as a {mode} point',
    'chart.autoDetect.noClearBottom': 'No clear bullish setup found. Using the lowest point.',
    'chart.autoDetect.noClearTop': 'No clear bearish setup found. Using the highest point.',
    'chart.autoDetect.noSuitableLow': 'No suitable low found. Try selecting a candle manually.',
    'chart.autoDetect.noSuitableHigh': 'No suitable high found. Try selecting a candle manually.',
    'chart.autoDetect.failed': 'Auto-detect failed. Try selecting a candle manually.',
    'chart.selectedTime': 'Selected time',
    'chart.entryRange': 'Entry range',
    'tooltip.time': 'Time',
    'tooltip.open': 'Open',
    'tooltip.high': 'High',
    'tooltip.low': 'Low',
    'tooltip.close': 'Close',
    'table.title': 'Projection Levels - {mode}',
    'table.line': 'Line',
    'table.price': 'Price',
    'table.type': 'Type',
    'table.status': 'Status',
    'table.type.target': 'Target',
    'table.type.pivot': 'Pivot',
    'table.status.reached': 'Reached',
    'table.status.notReached': 'Not reached',
    'tip.title': 'Trading note',
    'tip.desc.top': 'Pivots can act as bounce areas; targets help plan short take-profit/reduce.',
    'tip.desc.bottom': 'Pivots can act as pullback areas; targets help plan long take-profit/reduce.',
    'summary.title': 'Trend Summary',
    'summary.section.analysis': '{mode} analysis',
    'summary.section.action': 'Action idea',
    'summary.progress.targets': 'Target progress',
    'summary.progress.pivots': 'Pivot progress',
    'summary.reached.targets': '{reached}/{total} targets reached',
    'summary.reached.pivots': '{reached}/{total} pivots reached',
    'summary.disclaimer': 'Disclaimer: For reference only. Not investment advice.',
  },
  ja: {
    'app.title': 'トレンド予測システム',
    'app.subtitle': 'ローソク足と予測ラインによるトレンド分析',
    'app.disclaimer': '注意：暗号資産取引は高リスクです。本ツールは参考情報であり投資助言ではありません。',
    'lang.label': '言語',
    'lang.changed': '言語を切り替えました',
    'common.error': 'エラー',
    'common.close': '閉じる',
    'search.title': 'トレンド予測',
    'search.pair': '通貨ペア',
    'search.pairPlaceholder': '通貨ペアを選択 / 入力',
    'search.timeframe': '時間足',
    'search.limit': '本数',
    'search.search': '検索',
    'search.loading': '読み込み中…',
    'search.noMatch': '一致するペアが見つかりません',
    'search.noDataTitle': 'データなし',
    'search.noDataDesc': 'ローソク足データを取得できません。ペアまたは時間足を変更してください。',
    'search.loadDoneTitle': '読み込み完了',
    'search.loadDoneDesc': '{count} 本のローソク足を読み込みました。チャート上でポイントを選択できます。',
    'chart.title': 'ローソク足 - {mode} の手動選択',
    'chart.mode.bottom': '上昇',
    'chart.mode.top': '下落',
    'chart.autoDetect': '自動検出',
    'chart.reset': 'リセット',
    'chart.instructionsTitle': '使い方',
    'chart.instructions.1': '方向（上昇/下落）を選択',
    'chart.instructions.2': '「自動検出」をクリック',
    'chart.instructions.3': 'またはチャートの足をクリックして手動選択',
    'chart.instructions.4': '目標/転換点のラインを計算します',
    'chart.loadingTitle': '読み込み中',
    'chart.loadingDesc': 'データ取得中です。しばらくお待ちください…',
    'chart.selectedPoint': '{mode}ポイント',
    'chart.selectedMessage': '{index}番目の足を{mode}ポイントとして選択しました',
    'chart.autoDetect.noClearBottom': '明確な上昇パターンが見つかりませんでした。最安値ポイントを使用します。',
    'chart.autoDetect.noClearTop': '明確な下落パターンが見つかりませんでした。最高値ポイントを使用します。',
    'chart.autoDetect.noSuitableLow': '適切な安値が見つかりません。手動で選択してください。',
    'chart.autoDetect.noSuitableHigh': '適切な高値が見つかりません。手動で選択してください。',
    'chart.autoDetect.failed': '自動検出に失敗しました。手動で選択してください。',
    'chart.selectedTime': '選択時間',
    'chart.entryRange': 'エントリー範囲',
    'tooltip.time': '時間',
    'tooltip.open': '始値',
    'tooltip.high': '高値',
    'tooltip.low': '安値',
    'tooltip.close': '終値',
    'table.title': '予測ライン - {mode}',
    'table.line': 'ライン',
    'table.price': '価格',
    'table.type': '種別',
    'table.status': '状態',
    'table.type.target': '目標',
    'table.type.pivot': '転換点',
    'table.status.reached': '到達',
    'table.status.notReached': '未到達',
    'tip.title': 'メモ',
    'tip.desc.top': '転換点は反発候補；目標はショートの利確/縮小の参考。',
    'tip.desc.bottom': '転換点は押し目候補；目標はロングの利確/縮小の参考。',
    'summary.title': 'サマリー',
    'summary.section.analysis': '{mode}分析',
    'summary.section.action': '提案',
    'summary.progress.targets': '目標の進捗',
    'summary.progress.pivots': '転換点の進捗',
    'summary.reached.targets': '目標 {reached}/{total}',
    'summary.reached.pivots': '転換点 {reached}/{total}',
    'summary.disclaimer': '免責：参考情報であり投資助言ではありません。',
  },
};

